import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Grid,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
} from "@mui/material";
import "../../App.css";
import EditIcon from "@mui/icons-material/Edit";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { TableNodata } from "../../components/TableNodata";
import { TableLoader } from "../../components/TableLoader";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import CustomPagination from "../../components/CustomPagination";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import api from "../../api/client";
import { useQuery } from "@tanstack/react-query";

const InterviewQuestionBankList = () => {
    const [showTable, setShowTable] = useState(false);
    const location = useLocation();
    const { dashboard, viewAssessments } = location.state || "";

    const { showSnackbar } = useSnackbar();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const navigate = useNavigate();

    const headings = [
        { label: "S.No", align: "center" },
        { label: "Job Role", align: "left" },
        { label: "Interviewer", align: "left" },
        { label: "Questions", align: "center" },
        { label: "Question Type", align: "center" },
        { label: "Experience Level", align: "center" },
        { label: "Difficulty", align: "center" },
        { label: "Created Date", align: "left" },
        { label: "Actions", align: "center" },
    ];

    const fetchInterviewQuestionBankList = async () => {
        const payload = {
            page: page,
            size: rowsPerPage,
        };

        const res = await api.post("/InterviewQuestionBankSessionsList", payload);

        if (res?.data?.status === "success") {
            return {
                data: res.data.data || [],
                total: res.data.total || res.data.data?.length || 0,
            };
        } else {
            return {
                data: [],
                total: 0,
            };
        }
    };

    const { data: queryData, isLoading, refetch } = useQuery({
        queryKey: ["interviewQuestionBankList", page, rowsPerPage],
        queryFn: fetchInterviewQuestionBankList,
    });

    const listData = queryData?.data || [];
    const count = queryData?.total || 0;

    // Refetch list when component mounts or location changes
    useEffect(() => {
        refetch();
    }, [location.pathname]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowTable(true);
        }, 200);

        return () => clearTimeout(timer);
    }, []);

    const handleAddInterviewQuestionBank = () => {
        // Clear any previous streaming data
        localStorage.removeItem("generatedQuestions");
        localStorage.removeItem("sessionKeys");
        navigate("/addInterviewQuestionBank", {
            state: { newInterviewQuestionBank: true }
        });
    };

    const handleEdit = (id) => {
        navigate("/addInterviewQuestionBank", {
            state: {
                gen_id: id,
                edit: true,
            },
        });
    };

    const handleView = (id) => {
        navigate("/viewInterviewQuestionBank", {
            state: {
                gen_id: id,
            },
        });
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setShowTable(true);
        }, 200);

        return () => clearTimeout(timer);
    }, []);

    return (
        <Box sx={{ minHeight: "100vh", p: 1 }}>
            {(dashboard === true || viewAssessments === true) && (
                <Grid
                    item
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        color: "#1c97e3",
                        gap: "4px",
                        cursor: "pointer",
                        width: "fit-content",
                        mb: 2,
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            window.history.back();
                        }
                    }}
                    onClick={() => window.history.back()}
                >
                    <ArrowBackIcon sx={{ fontSize: "17px" }} />
                    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1c97e3" }}>
                        Back
                    </Typography>
                </Grid>
            )}

            <>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        pb: 2,
                        mb: 2,
                    }}
                >
                    <Grid>
                        <Grid sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}>
                            <Box className="icon-box">
                                <DescriptionOutlinedIcon className="iconHeading" />
                            </Box>
                            <Box sx={{ ml: 2 }}>
                                <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}>
                                    Interview Question Bank
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                    <Grid sx={{ display: "flex", gap: 4 }}>
                        <Button
                            variant="contained"
                            className="gradient-button"
                            onClick={handleAddInterviewQuestionBank}
                            startIcon={<AddIcon sx={{ color: "white" }} />}
                        >
                            <Typography sx={{ color: "white" }}>Interview Question Bank</Typography>
                        </Button>
                    </Grid>
                </Box>

                <TableContainer
                    component={Paper}
                    sx={{
                        opacity: showTable ? 1 : 0,
                        transform: showTable ? "translateY(0)" : "translateY(40px)",
                        transition: "opacity 0.5s ease, transform 0.5s ease",
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                {headings.map((heading, index) => (
                                    <TableCell key={index} align={heading.align} className="tableHeader">
                                        <b>{heading.label}</b>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody className="tablebody">
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={headings.length} align="center" sx={{ py: 4 }}>
                                        <TableLoader message="Fetching interview question banks..." />
                                    </TableCell>
                                </TableRow>
                            ) : listData?.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={headings.length} align="center" sx={{ py: 10 }}>
                                        <TableNodata />
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {listData.map((item, index) => (
                                        <TableRow
                                            key={item.gen_id}
                                            hover
                                            sx={{
                                                backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                                                "&:hover": { backgroundColor: "#f1f1f1" },
                                            }}
                                        >
                                            <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>

                                            {/* Job Role */}
                                            <TableCell sx={{ maxWidth: '200px' }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                                    {item.program_code && (
                                                        <Typography sx={{ fontWeight: 700, fontSize: '12px', color: '#1c97e3', letterSpacing: '0.3px' }}>
                                                            {item.program_code || "-"}
                                                        </Typography>
                                                    )}
                                                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#1a1a1a', wordBreak: 'break-word', lineHeight: 1.3 }}>
                                                        {item.program_name || "-"}
                                                    </Typography>
                                                </Box>
                                            </TableCell>

                                            {/* Interviewer */}
                                            <TableCell sx={{ color: "#546e7a" }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2 }}>
                                                    <Typography sx={{ fontWeight: 600, fontSize: '13px', color: '#1a1a1a' }}>
                                                        {item.interviewer_name || '-'}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                                                        {item.interviewer_email || '-'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>

                                            {/* Total Questions */}
                                            <TableCell align="center" sx={{ fontWeight: 600, color: "#1a1a1a" }}>
                                                {item.total_questions || 0}
                                            </TableCell>

                                            {/* Question Type */}
                                            <TableCell align="center">
                                                <Chip
                                                    label={item.question_type || '-'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: "#e3f2fd",
                                                        color: "#1565c0",
                                                        fontWeight: 600,
                                                        fontSize: "12px",
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Experience Level */}
                                            <TableCell align="center">
                                                <Chip
                                                    label={item.experience_level || '-'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: "#f3e5f5",
                                                        color: "#7b1fa2",
                                                        fontWeight: 600,
                                                        fontSize: "12px",
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Difficulty */}
                                            <TableCell align="center">
                                                <Chip
                                                    label={item.difficulty || '-'}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: "#fff3e0",
                                                        color: "#e65100",
                                                        fontWeight: 600,
                                                        fontSize: "12px",
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Created Date */}
                                            <TableCell sx={{ color: "#546e7a", fontSize: '13px' }}>
                                                {item.created_at ? new Date(item.created_at).toLocaleDateString() : '-'}
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    padding: "10px 10px",
                                                    whiteSpace: "nowrap",
                                                    width: "1%",
                                                }}
                                            >
                                                <Box display="flex" justifyContent="center" alignItems="center" gap={2} sx={{cursor:'pointer'}}
                                                    onClick={() => handleView(item.gen_id)}
                                                >
                                                    <Typography color="primary" sx={{ fontSize: "14px", fontWeight: 900 }}>
                                                        View
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                    {((page === 0 && listData?.length > rowsPerPage - 1) || page !== 0) && (
                                        <CustomPagination
                                            page={page}
                                            setPage={setPage}
                                            count={count}
                                            rowsPerPage={rowsPerPage}
                                            colSpan={headings.length}
                                        />
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </>
        </Box>
    );
};

export default InterviewQuestionBankList;
